import React, { useState, useEffect } from "react";
import {
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Stack,
    Typography,
} from "@mui/material";
import { useTranslation } from "shared/hooks/useTranslation";
import ResponsiveDialog from "shared/ui/ResponsiveDialog";
import SubmitButton from "shared/ui/SubmitButton";
import ConfirmDialog from "shared/ui/ConfirmDialog";
import type { LiquidityPool } from "features/wallet/api/Wallet";

const CURRENCIES = ["ARS", "USD", "USDT", "USDC"] as const;

export interface EditLiquidityPoolModalProps {
    open: boolean;
    pool: LiquidityPool | null;
    onClose: () => void;
    onSubmit: (poolId: string, data: { name?: string; currency?: string }) => Promise<{ success: boolean; error?: string }>;
    mutationsDisabled?: boolean;
}

const EditLiquidityPoolModal: React.FC<EditLiquidityPoolModalProps> = ({
    open,
    pool,
    onClose,
    onSubmit,
    mutationsDisabled = false,
}) => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [currency, setCurrency] = useState("ARS");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);

    useEffect(() => {
        if (pool) {
            setName(pool.name);
            setCurrency(pool.currency);
        }
    }, [pool]);

    const requestClose = () => {
        if (submitting) return;
        if (pool && (name.trim() !== pool.name || currency !== pool.currency)) {
            setConfirmDiscard(true);
            return;
        }
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (!pool) return;
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError(t("validation.required"));
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const result = await onSubmit(pool._id, { name: trimmedName, currency });
            if (result.success) {
                setError(null);
                onClose();
            } else {
                setError(result.error ?? t("common.error"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!pool) return null;

    return (
        <>
        <ResponsiveDialog open={open} onClose={requestClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t("wallet.editPool")}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label={t("wallet.poolName")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        autoFocus
                    />
                    <FormControl fullWidth>
                        <InputLabel>{t("forms.currency")}</InputLabel>
                        <Select
                            value={currency}
                            label={t("forms.currency")}
                            onChange={(e) => setCurrency(e.target.value)}
                        >
                            {CURRENCIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary">
                        {t("wallet.balanceNotEditable")}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={requestClose} disabled={submitting}>
                    {t("common.cancel")}
                </Button>
                <SubmitButton onClick={handleSubmit} variant="contained" loading={submitting} disabled={mutationsDisabled}>
                    {t("common.save")}
                </SubmitButton>
            </DialogActions>
        </ResponsiveDialog>
        <ConfirmDialog
            open={confirmDiscard}
            title={t("forms.discardChangesTitle")}
            description={t("forms.discardChangesDescription")}
            cancelLabel={t("forms.keepEditing")}
            confirmLabel={t("forms.discardDraft")}
            confirmColor="error"
            onCancel={() => setConfirmDiscard(false)}
            onConfirm={() => {
                setConfirmDiscard(false);
                if (pool) {
                    setName(pool.name);
                    setCurrency(pool.currency);
                }
                setError(null);
                onClose();
            }}
        />
        </>
    );
};

export default EditLiquidityPoolModal;
